import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { usePermissions, PERMISSIONS, MENU_KEYS } from "../hooks/usePermissions";
import { Button } from "./ui/button";
import { VersionBadge } from "./VersionBadge";
import InboundCallManager from "./InboundCallManager";
import RecentActivityPanel from "./RecentActivityPanel";
import PhoneDialer from "./PhoneDialer";
import { 
  ClipboardList, 
  History, 
  Users, 
  FileText, 
  LogOut, 
  Menu, 
  X,
  Settings,
  Building2,
  ScrollText,
  LayoutDashboard,
  Cog,
  Calendar,
  Trophy,
  Mail,
  FileUser,
  UserCircle,
  FileStack,
  Briefcase,
  UserPlus,
  FileType,
  CheckSquare,
  Share2,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  Users2,
  Inbox,
  Phone,
  Bot,
  Zap,
  BookOpen
} from "lucide-react";

export default function DashboardLayout() {
  const { user, logout, isAdmin, isTeamLeader } = useAuth();
  const { hasPermission, canAccessMenu, loading: permissionsLoading } = usePermissions();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [dialerOpen, setDialerOpen] = useState(false);
  
  // Collapsible menu state - persist CRM expanded state
  const [logsExpanded, setLogsExpanded] = useState(true);
  const [crmExpanded, setCrmExpanded] = useState(() => {
    // Check localStorage for persisted state, default to true for CRM access users
    const saved = localStorage.getItem('crmSectionExpanded');
    return saved !== null ? saved === 'true' : true;
  });
  const [franchiseExpanded, setFranchiseExpanded] = useState(() => {
    const saved = localStorage.getItem('franchiseSectionExpanded');
    return saved !== null ? saved === 'true' : true;
  });

  // Persist CRM expanded state
  useEffect(() => {
    localStorage.setItem('crmSectionExpanded', crmExpanded.toString());
  }, [crmExpanded]);

  // Persist Franchise expanded state
  useEffect(() => {
    localStorage.setItem('franchiseSectionExpanded', franchiseExpanded.toString());
  }, [franchiseExpanded]);

  // Permission-based access checks (use RBAC first, fallback to team-based for legacy)
  const userTeamNames = user?.team_names || [];
  
  // CRM access: ALL authenticated users have access
  const hasCRMAccess = true;

  // Franchise access: ALL authenticated users have access
  const hasFranchiseAccess = true;

  // Document access: ALL users
  const hasDocumentAccess = true;
  
  // Template access: ALL users
  const hasTemplateAccess = true;
  
  // Social media access: ALL users
  const hasSocialAccess = true;
  
  // Admin access for user management, teams, settings, backup
  const hasUserManagementAccess = hasPermission(PERMISSIONS.ADMIN_USERS_READ);
  const hasTeamManagementAccess = hasPermission(PERMISSIONS.ADMIN_TEAMS_READ);
  const hasSettingsAccess = hasPermission(PERMISSIONS.ADMIN_SETTINGS_READ);
  const hasBackupAccess = hasPermission(PERMISSIONS.ADMIN_BACKUP_READ);

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const formatDateTime = (date) => {
    const options = { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    };
    return date.toLocaleString('en-US', options);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Dashboard for admins and team leaders
  const dashboardNavItem = { to: "/app/dashboard", icon: LayoutDashboard, label: "Dashboard" };

  // Logs section items
  const logsNavItems = [
    { to: "/app/log", icon: ClipboardList, label: "Daily Log" },
    { to: "/app/history", icon: History, label: "History" },
    { to: "/app/leaderboard", icon: Trophy, label: "Leaderboard" },
  ];

  // CRM section items (only for users with CRM access)
  const crmNavItems = [
    { to: "/admin/companies", icon: Briefcase, label: "Companies" },
    { to: "/admin/leads", icon: UserPlus, label: "Leads" },
    { to: "/admin/lists", icon: FolderOpen, label: "Lists" },
    { to: "/admin/inbox", icon: Inbox, label: "Inbox" },
    { to: "/admin/tasks", icon: CheckSquare, label: "Tasks" },
    { to: "/admin/ai-calls", icon: Bot, label: "AI Calls" },
    { to: "/admin/sequences", icon: Zap, label: "Sequences" },
    { to: "/admin/knowledge-base", icon: BookOpen, label: "Knowledge Base" },
  ];

  // Other nav items - filtered by permissions
  const otherNavItems = [
    hasDocumentAccess && { to: "/documents", icon: FolderOpen, label: "Documents" },
    hasTemplateAccess && { to: "/admin/templates", icon: FileType, label: "Templates" },
    hasSocialAccess && { to: "/admin/social", icon: Share2, label: "Social" },
    { to: "/app/profile", icon: Settings, label: "Profile" },
  ].filter(Boolean);

  // Team leader specific items
  const teamLeaderNavItems = [
    { to: "/app/team-logs", icon: Users, label: "Team Logs" },
    { to: "/admin/logs", icon: ScrollText, label: "All Logs" },
    { to: "/admin/reports", icon: FileText, label: "Reports" },
    { to: "/admin/user-report", icon: FileUser, label: "User Report" },
    { to: "/admin/user-dashboard", icon: UserCircle, label: "User Dashboard" },
  ];

  // Admin specific items - filtered by permissions
  const adminNavItems = [
    hasUserManagementAccess && { to: "/admin/users", icon: Users, label: "Users" },
    hasTeamManagementAccess && { to: "/admin/teams", icon: Building2, label: "Teams" },
    isAdmin && { to: "/admin/forms", icon: FileStack, label: "Forms" },
    { to: "/admin/logs", icon: ScrollText, label: "All Logs" },
    { to: "/admin/reports", icon: FileText, label: "Reports" },
    { to: "/admin/user-report", icon: FileUser, label: "User Report" },
    { to: "/admin/user-dashboard", icon: UserCircle, label: "User Dashboard" },
    { to: "/admin/email-logs", icon: Mail, label: "Email Logs" },
    hasSettingsAccess && { to: "/admin/settings", icon: Cog, label: "Settings" },
  ].filter(Boolean);

  const NavItem = ({ to, icon: Icon, label }) => (
    <NavLink
      to={to}
      onClick={() => setSidebarOpen(false)}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 rounded-md transition-colors ${
          isActive
            ? "bg-primary text-primary-foreground"
            : "text-slate-600 hover:bg-slate-100"
        }`
      }
    >
      <Icon className="h-4 w-4" />
      <span className="font-medium text-sm">{label}</span>
    </NavLink>
  );

  const CollapsibleSection = ({ title, icon: Icon, expanded, onToggle, children, badge }) => (
    <div className="mb-2">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-slate-100 rounded-md transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-blue-600" />
          <span className="font-bold text-blue-600">{title}</span>
          {badge && (
            <span className="px-1.5 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-700 rounded">
              {badge}
            </span>
          )}
        </div>
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-blue-500" />
        ) : (
          <ChevronRight className="h-4 w-4 text-blue-500" />
        )}
      </button>
      {expanded && (
        <div className="ml-2 mt-1 space-y-0.5 border-l-2 border-blue-200 pl-2">
          {children}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50/30">
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 header-glass z-50 flex items-center px-4">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 rounded-md hover:bg-slate-100"
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
        <Link to={isAdmin || isTeamLeader ? "/app/dashboard" : "/app/log"}>
          <img src="/logo.png" alt="DailyLog" className="h-8 ml-4 cursor-pointer hover:opacity-80 transition-opacity" />
        </Link>
      </div>

      {/* Sidebar Overlay (Mobile) */}
      {sidebarOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-full w-64 bg-white border-r border-slate-200 z-50 transform transition-transform duration-200 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } md:translate-x-0`}
      >
        <div className="flex flex-col h-full">
          {/* Logo and Dialer */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-slate-100">
            <Link to={isAdmin || isTeamLeader ? "/app/dashboard" : "/app/log"} data-testid="sidebar-logo-link">
              <img src="/logo.png" alt="DailyLog" className="h-10 cursor-pointer hover:opacity-80 transition-opacity" />
            </Link>
            
            {/* Phone Dialer Icon */}
            <button
              onClick={() => setDialerOpen(true)}
              className="p-2 rounded-lg bg-green-50 hover:bg-green-100 text-green-600 transition-colors"
              title="Open Phone Dialer"
              data-testid="dialer-button"
            >
              <Phone className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {/* Dashboard for Admin/Team Leader */}
            {(isAdmin || isTeamLeader) && (
              <div className="mb-3">
                <p className="px-4 py-1.5 text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Overview
                </p>
                <NavItem {...dashboardNavItem} />
              </div>
            )}

            {/* Main Section Header */}
            <p className="px-4 py-1.5 text-xs font-medium text-slate-400 uppercase tracking-wider">
              Main
            </p>

            {/* Logs Section - Collapsible */}
            <CollapsibleSection
              title="Logs"
              icon={FolderOpen}
              expanded={logsExpanded}
              onToggle={() => setLogsExpanded(!logsExpanded)}
            >
              {logsNavItems.map((item) => (
                <NavItem key={item.to} {...item} />
              ))}
            </CollapsibleSection>

            {/* CRM Section - Collapsible (only for users with CRM access) */}
            {hasCRMAccess && (
              <CollapsibleSection
                title="CRM"
                icon={Users2}
                expanded={crmExpanded}
                onToggle={() => setCrmExpanded(!crmExpanded)}
                badge={isAdmin ? null : "JobTalk"}
              >
                {crmNavItems.map((item) => (
                  <NavItem key={item.to} {...item} />
                ))}
              </CollapsibleSection>
            )}

            {/* Other Items */}
            {otherNavItems.map((item) => (
              <NavItem key={item.to} {...item} />
            ))}

            {/* Team Leader Section */}
            {isTeamLeader && !isAdmin && (
              <>
                <p className="px-4 py-1.5 mt-4 text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Team Leader
                </p>
                {teamLeaderNavItems.map((item) => (
                  <NavItem key={item.to} {...item} />
                ))}
              </>
            )}

            {/* Admin Section */}
            {isAdmin && (
              <>
                <p className="px-4 py-1.5 mt-4 text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Admin
                </p>
                {adminNavItems.map((item) => (
                  <NavItem key={item.to} {...item} />
                ))}
              </>
            )}
          </nav>

          {/* User Section */}
          <div className="p-3 border-t border-slate-100">
            {/* Date and Time */}
            <div className="flex items-center gap-2 px-4 py-2 mb-2 text-xs text-slate-500">
              <Calendar className="h-3.5 w-3.5" />
              <span>{formatDateTime(currentDateTime)}</span>
            </div>
            
            <div className="flex items-center gap-3 px-3 py-2.5 bg-slate-50 rounded-lg">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{user?.full_name}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                {user?.phone && (
                  <p className="text-xs text-slate-600 truncate mt-0.5 flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    {user.phone.replace(/^1?(\d{3})(\d{3})(\d{4})$/, '$1-$2-$3')}
                  </p>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="shrink-0"
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="md:ml-64 min-h-screen pt-16 md:pt-0">
        <div className="p-6 md:p-8 max-w-[1600px]">
          <Outlet />
        </div>
      </main>
      
      {/* Version Badge - Fixed Position */}
      <VersionBadge />
      
      {/* Inbound Call Manager - handles incoming calls globally */}
      {hasCRMAccess && <InboundCallManager />}
      
      {/* Recent Activity Panel - slide-out sidebar for quick access */}
      {hasCRMAccess && <RecentActivityPanel />}
      
      {/* Phone Dialer */}
      <PhoneDialer isOpen={dialerOpen} onClose={() => setDialerOpen(false)} />
    </div>
  );
}
